import React, { useEffect, useMemo, useRef, useState } from 'react';
import useApiRequest from './useApiRequest';
import { domain } from './Domain';
import '../css/CategoryComponent.css';
import ProductForm, { notify } from './ProductForm';
import { DNA } from 'react-loader-spinner';

const ProductList = ({ products, onDelete, onEdit, loading }) => {
    if (loading) {
        return (
            <p className="lookup-loading">
                <DNA
                    visible
                    height="56"
                    width="56"
                    ariaLabel="loading products"
                    wrapperClass="lookup-dna"
                />
                <span>Loading products...</span>
            </p>
        );
    }

    if (products.length === 0) {
        return <p className="lookup-empty mb-0">No products found.</p>;
    }

    return (
        <ul className="lookup-items lookup-items--products list-unstyled mb-0">
            {products.map((product) => (
                <li key={product.id} className="lookup-item lookup-item--product">
                    <div className="lookup-product-left">
                        <img
                            className="lookup-product-image"
                            src={domain + product.image}
                            alt={product.product_name}
                        />
                        <div className="lookup-product-copy">
                            <p className="lookup-product-name">{product.product_name}</p>
                            <p className="lookup-product-meta mb-0">
                                {product.part_number
                                    ? `Part: ${product.part_number}`
                                    : 'No part number'}
                                {' · '}
                                {product.condition}
                            </p>
                        </div>
                    </div>
                    <div className="lookup-item-actions">
                        <button
                            type="button"
                            onClick={() => onDelete(product.id)}
                            className="btn btn-sm btn-outline-danger"
                        >
                            Delete
                        </button>
                        <button
                            type="button"
                            onClick={() => onEdit(product)}
                            className="btn btn-sm btn-outline-primary"
                        >
                            Edit
                        </button>
                    </div>
                </li>
            ))}
        </ul>
    );
};

const ProductComponent = () => {
    const [editingId, setEditingId] = useState(null);
    const [products, setProducts] = useState([]);
    const [selected, setSelected] = useState(null);
    const [image, setImage] = useState(null);
    const [coverImage, setCoverImage] = useState(null);
    const [datasheet, setDatasheet] = useState(null);
    const [images, setImages] = useState([]);
    const [datasheets, setDatasheets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [assetBusy, setAssetBusy] = useState(false);
    const coverRef = useRef(null);
    const imageRef = useRef(null);
    const { postRequest, getRequest, putRequest, deleteRequest } = useApiRequest();

    useEffect(() => {
        fetchProducts();
    }, []);

    useEffect(() => {
        if (!editingId) return;
        fetchImages();
        fetchDatasheets();
    }, [editingId]);

    const fetchProducts = async () => {
        const response = await getRequest(`${domain}/products`);
        setProducts(Array.isArray(response) ? response : []);
        setLoading(false);
    };

    const fetchImages = async () => {
        const response = await getRequest(`${domain}/images?product=${editingId}`);
        setImages(Array.isArray(response) ? response : []);
    };

    const fetchDatasheets = async () => {
        const response = await getRequest(`${domain}/datasheets?product=${editingId}`);
        setDatasheets(Array.isArray(response) ? response : []);
    };

    const selectedDisplay = useMemo(
        () => products.find((item) => item.id === editingId) || selected,
        [products, editingId, selected]
    );

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this item?')) return;
        const response = await deleteRequest(`${domain}/products/${id}`);
        if (response) {
            setProducts((prev) => prev.filter((product) => product.id !== id));
        }
    };

    const handleEdit = (product) => {
        setEditingId(product.id);
        setSelected(product);
        setCoverImage(null);
        setImage(null);
        setDatasheet(null);
    };

    const handleAddImage = async () => {
        if (!image) {
            notify('Please choose an image first.', 'error');
            return;
        }
        setAssetBusy(true);
        try {
            const formData = new FormData();
            formData.append('image', image);
            formData.append('product', String(editingId));
            const response = await postRequest(`${domain}/images`, formData);
            if (response) {
                notify('Image added successfully', 'success');
                setImage(null);
                await fetchImages();
            }
        } finally {
            setAssetBusy(false);
        }
    };

    const handleDeleteImage = async (id) => {
        if (!window.confirm('Are you sure you want to delete this image?')) return;
        const response = await deleteRequest(`${domain}/images/${id}`);
        if (response) fetchImages();
    };

    const handleAddDatasheet = async () => {
        if (!datasheet) {
            notify('Please choose a datasheet first.', 'error');
            return;
        }
        setAssetBusy(true);
        try {
            const formData = new FormData();
            formData.append('datasheet', datasheet);
            formData.append('product', String(editingId));
            const response = await postRequest(`${domain}/datasheets`, formData);
            if (response) {
                notify('Datasheet added successfully', 'success');
                setDatasheet(null);
                await fetchDatasheets();
            }
        } finally {
            setAssetBusy(false);
        }
    };

    const handleDeleteDatasheet = async (id) => {
        if (!window.confirm('Are you sure you want to delete this datasheet?')) return;
        const response = await deleteRequest(`${domain}/datasheets/${id}`);
        if (response) fetchDatasheets();
    };

    const handleCoverImage = async () => {
        if (!coverImage) {
            notify('Please choose a new cover image first.', 'error');
            return;
        }
        setAssetBusy(true);
        try {
            const formData = new FormData();
            formData.append('image', coverImage);
            const response = await putRequest(
                `${domain}/products/${editingId}`,
                formData
            );
            if (response) {
                notify('Cover image updated successfully', 'success');
                setCoverImage(null);
                if (imageRef.current) imageRef.current.src = domain + response.image;
                fetchProducts();
            }
        } finally {
            setAssetBusy(false);
        }
    };

    const stopEditing = () => {
        setEditingId(null);
        setSelected(null);
        setImage(null);
        setCoverImage(null);
        setDatasheet(null);
        setImages([]);
        setDatasheets([]);
    };

    return (
        <section className="lookup-manager lookup-manager--product">
            {editingId ? (
                <>
                    <div className="lookup-editing-header">
                        <h3 className="lookup-manager-title mb-0">Editing product</h3>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={stopEditing}
                        >
                            Cancel editing
                        </button>
                    </div>
                    <p className="lookup-manager-meta mb-3">
                        {selectedDisplay?.product_name || `Product #${editingId}`}
                    </p>

                    <ProductForm
                        editingId={editingId}
                        selected={selected}
                        fetchProducts={fetchProducts}
                    />

                    <div className="lookup-assets-grid">
                        <article className="lookup-asset-card">
                            <h4 className="lookup-asset-title">Cover image</h4>
                            <div className="lookup-cover-block">
                                <img
                                    ref={imageRef}
                                    src={domain + selectedDisplay.image}
                                    alt={selectedDisplay.product_name}
                                />
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                ref={coverRef}
                                hidden
                                onChange={(e) => setCoverImage(e.target.files?.[0] || null)}
                            />
                            <div className="lookup-asset-actions">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => coverRef.current?.click()}
                                >
                                    Choose cover image
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    disabled={!coverImage || assetBusy}
                                    onClick={handleCoverImage}
                                >
                                    {assetBusy ? 'Updating...' : 'Update cover'}
                                </button>
                            </div>
                        </article>

                        <article className="lookup-asset-card">
                            <h4 className="lookup-asset-title">Gallery images</h4>
                            <input
                                type="file"
                                className="form-control form-control-sm"
                                accept="image/*"
                                onChange={(e) => setImage(e.target.files?.[0] || null)}
                            />
                            <button
                                type="button"
                                className="btn btn-sm btn-primary mt-2"
                                disabled={!image || assetBusy}
                                onClick={handleAddImage}
                            >
                                {assetBusy ? 'Saving...' : 'Add image'}
                            </button>
                            <div className="lookup-image-grid">
                                {images.map((img) => (
                                    <div key={img.id} className="lookup-image-tile">
                                        <img src={domain + img.image} alt="product" />
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => handleDeleteImage(img.id)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </article>

                        <article className="lookup-asset-card">
                            <h4 className="lookup-asset-title">Datasheets</h4>
                            <input
                                type="file"
                                className="form-control form-control-sm"
                                accept="application/pdf"
                                onChange={(e) =>
                                    setDatasheet(e.target.files?.[0] || null)
                                }
                            />
                            <button
                                type="button"
                                className="btn btn-sm btn-primary mt-2"
                                disabled={!datasheet || assetBusy}
                                onClick={handleAddDatasheet}
                            >
                                {assetBusy ? 'Saving...' : 'Add datasheet'}
                            </button>
                            <ul className="lookup-datasheet-list list-unstyled mb-0 mt-3">
                                {datasheets.map((item) => (
                                    <li key={item.id} className="lookup-datasheet-row">
                                        <a
                                            href={domain + item.datasheet}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <i
                                                className="fa-solid fa-file-pdf text-danger me-2"
                                                aria-hidden
                                            />
                                            {item.datasheet
                                                .replace('datasheets/', '')
                                                .replace('/media/', '')}
                                        </a>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() =>
                                                handleDeleteDatasheet(item.id)
                                            }
                                        >
                                            Remove
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </article>
                    </div>
                </>
            ) : (
                <>
                    <header className="lookup-manager-header">
                        <h3 className="lookup-manager-title">Products</h3>
                        <p className="lookup-manager-meta mb-0">
                            {products.length} item{products.length !== 1 ? 's' : ''}
                        </p>
                    </header>
                    <ProductList
                        products={products}
                        onDelete={handleDelete}
                        onEdit={handleEdit}
                        loading={loading}
                    />
                </>
            )}
        </section>
    );
};

export default ProductComponent;
